import React, { useEffect, useMemo, useState } from "react";
import { DEFAULT_SCOUTS, getScoutCredentials, saveScoutCredentials } from "../scouts/ScoutAuth";

const OPPORTUNITY_KEY = "matrixos.scout.opportunities";
const COMMISSION_DEFAULT = 10;

const statusFlow = ["Submitted", "HQ Review", "Qualified", "Meeting Scheduled", "Proposal Sent", "Client Won", "Commission Active", "Rejected"];

const starterReviews = [
  {
    id: "super-greenleaf",
    name: "Greenleaf Dispensary",
    owner: "Sarah Jacobs",
    layer: "Morpheus",
    stage: "Qualified",
    score: 9,
    valueNumber: 1699,
    commissionPercent: 10,
    monthlyCommission: 170,
    challenge: "Stock accountability and follow-through gaps across the site.",
    directQuote: "We never know what the real stock position is until it becomes a problem.",
    observations: "Good fit for Morpheus and possible bundle expansion after first demo.",
    nextAction: "Prepare founder handoff or meeting scheduling.",
    hqNotes: [
      { by: "MatrixOS HQ", date: "Today", text: "Qualified for founder review. Confirm decision maker before demo." },
    ],
    updatedAt: "Today",
  },
  {
    id: "super-urban",
    name: "Urban Bliss Café",
    owner: "Thabo M.",
    layer: "Neo",
    stage: "Submitted",
    score: 7,
    valueNumber: 699,
    commissionPercent: 10,
    monthlyCommission: 70,
    challenge: "Opening duties and daily execution are inconsistent.",
    directQuote: "Sometimes customers wait while the team is still preparing.",
    observations: "Needs stronger evidence and manager confirmation.",
    nextAction: "Awaiting first MatrixOS review.",
    hqNotes: [],
    updatedAt: "Yesterday",
  },
];

function money(value) {
  const number = Number(value || 0);
  return `R${number.toLocaleString("en-ZA")}`.replace(/,/g, " ");
}

function getValueNumber(item) {
  if (typeof item.valueNumber === "number") return item.valueNumber;
  if (typeof item.monthlyValue === "number") return item.monthlyValue;
  if (typeof item.value === "number") return item.value;
  if (typeof item.value === "string") return Number(item.value.replace(/[^0-9.]/g, "")) || 0;
  const layer = String(item.layer || "Neo").toLowerCase();
  if (layer.includes("morpheus")) return 999;
  if (layer.includes("link")) return 499;
  if (layer.includes("bundle") || layer.includes("matrixos")) return 1699;
  return 699;
}

function normalizeOpportunity(item, index = 0) {
  const valueNumber = getValueNumber(item);
  const commissionPercent = Number(item.commissionPercent ?? COMMISSION_DEFAULT);
  const monthlyCommission = Math.round(valueNumber * (commissionPercent / 100));
  return {
    id: item.id || `opp-${index}`,
    name: item.name || item.businessName || "Unnamed opportunity",
    owner: item.owner || item.contactPerson || "Not captured",
    layer: item.layer || "Neo",
    stage: item.stage || item.status || "Submitted",
    score: Number(item.score ?? item.qualificationScore ?? 0),
    valueNumber,
    commissionPercent,
    monthlyCommission,
    challenge: item.challenge || item.biggestChallenge || "No challenge captured yet.",
    directQuote: item.directQuote || "No direct quote captured yet.",
    observations: item.observations || item.candidateObservations || "No scout observations captured yet.",
    nextAction: item.nextAction || "Review the submission and choose the next HQ action.",
    hqNotes: Array.isArray(item.hqNotes) ? item.hqNotes : [],
    evidence: item.evidence || [],
    evidenceFiles: item.evidenceFiles || [],
    industry: item.industry || "Not captured",
    address: item.address || "Not captured",
    role: item.role || item.position || "Not captured",
    phone: item.phone || "Not captured",
    email: item.email || "Not captured",
    scoutName: item.scoutName || item.scout || "Alex Johnson",
    updatedAt: item.updatedAt || "Just now",
  };
}

function loadScoutOpportunities() {
  if (typeof window === "undefined") return starterReviews;
  try {
    const saved = JSON.parse(window.localStorage.getItem(OPPORTUNITY_KEY) || "[]");
    const rows = Array.isArray(saved) ? saved.map(normalizeOpportunity) : [];
    const merged = [...rows, ...starterReviews.filter((starter) => !rows.some((row) => row.id === starter.id))];
    return merged.length ? merged : starterReviews;
  } catch (_) {
    return starterReviews;
  }
}

function saveScoutOpportunities(rows) {
  if (typeof window === "undefined") return;
  try {
    const clean = rows.filter((row) => !String(row.id).startsWith("super-")).map((row) => ({ ...row, value: money(row.valueNumber) }));
    window.localStorage.setItem(OPPORTUNITY_KEY, JSON.stringify(clean));
  } catch (_) {}
}

function badgeClass(stage) {
  if (stage === "Rejected") return "border-rose-200 bg-rose-50 text-rose-700";
  if (stage === "Commission Active" || stage === "Client Won") return "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (stage === "Qualified" || stage === "Meeting Scheduled") return "border-cyan-200 bg-cyan-50 text-cyan-700";
  if (stage === "HQ Review") return "border-amber-200 bg-amber-50 text-amber-700";
  return "border-blue-200 bg-blue-50 text-blue-700";
}

function StatCard({ label, value, hint }) {
  return <div className="rounded-2xl border border-white/10 bg-slate-900/70 p-5"><div className="text-xs font-black uppercase tracking-[0.2em] text-slate-400">{label}</div><div className="mt-2 text-2xl font-black text-white">{value}</div><div className="mt-1 text-xs text-slate-500">{hint}</div></div>;
}

function InfoBox({ label, value }) {
  return <div className="rounded-2xl border border-white/10 bg-slate-950/60 p-4"><div className="text-[0.65rem] font-black uppercase tracking-[0.16em] text-slate-500">{label}</div><div className="mt-1 font-black text-white">{value}</div></div>;
}

export default function ScoutPortalAdminPage() {
  const [view, setView] = useState("Review Queue");
  const [scouts, setScouts] = useState(DEFAULT_SCOUTS);
  const [saved, setSaved] = useState(false);
  const [opportunities, setOpportunities] = useState(starterReviews);
  const [selectedId, setSelectedId] = useState(starterReviews[0].id);
  const [filter, setFilter] = useState("All");
  const [note, setNote] = useState("");

  useEffect(() => {
    setScouts(getScoutCredentials());
    const rows = loadScoutOpportunities();
    setOpportunities(rows);
    setSelectedId(rows[0]?.id || "");
  }, []);

  const selected = opportunities.find((item) => item.id === selectedId) || opportunities[0];
  const visible = filter === "All" ? opportunities : opportunities.filter((item) => item.stage === filter);

  const totals = useMemo(() => {
    const active = opportunities.filter((o) => o.stage !== "Rejected");
    const pipeline = active.reduce((sum, item) => sum + item.valueNumber, 0);
    const commission = active.reduce((sum, item) => sum + item.monthlyCommission, 0);
    return {
      queue: opportunities.filter((o) => ["Submitted", "HQ Review"].includes(o.stage)).length,
      qualified: opportunities.filter((o) => ["Qualified", "Meeting Scheduled", "Proposal Sent"].includes(o.stage)).length,
      won: opportunities.filter((o) => ["Client Won", "Commission Active"].includes(o.stage)).length,
      pipeline,
      commission,
    };
  }, [opportunities]);

  function updateScout(index, field, value) {
    setScouts((rows) => rows.map((row, i) => i === index ? { ...row, [field]: value } : row));
    setSaved(false);
  }

  function addScout() {
    setScouts((rows) => [...rows, { id: `scout-${Date.now()}`, name: "New Scout", email: "", password: "", level: "Level 1", role: "Operational Scout", region: "Cape Town", status: "Active" }]);
    setSaved(false);
  }

  function removeScout(index) {
    setScouts((rows) => rows.filter((_, i) => i !== index));
    setSaved(false);
  }

  function saveCredentials() {
    saveScoutCredentials(scouts);
    setScouts(getScoutCredentials());
    setSaved(true);
    window.setTimeout(() => setSaved(false), 2500);
  }

  function updateOpportunity(id, patch) {
    const next = opportunities.map((row) => row.id === id ? { ...row, ...patch, updatedAt: "Just now" } : row);
    setOpportunities(next);
    saveScoutOpportunities(next);
  }

  function addNote(type = "HQ note") {
    if (!selected || !note.trim()) return;
    const hqNotes = [...(selected.hqNotes || []), { by: type, date: new Date().toLocaleDateString(), text: note.trim() }];
    const stagePatch = type === "Request Info" ? { stage: "HQ Review", nextAction: "Scout must add more clarity or evidence." } : {};
    updateOpportunity(selected.id, { hqNotes, ...stagePatch });
    setNote("");
  }

  return (
    <div className="space-y-6">
      <section className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 shadow-[0_24px_80px_rgba(0,0,0,.25)]">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-xs font-black uppercase tracking-[0.3em] text-cyan-300">MatrixOS Scout Portal</div>
            <h1 className="mt-3 text-3xl font-black tracking-[-0.04em]">Scouts Portal Control</h1>
            <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-300">Review scout submissions, request clarity, qualify opportunities, set value and activate commission.</p>
          </div>
          <div className="flex rounded-full border border-white/10 bg-slate-950/70 p-1 text-sm font-black">
            {["Review Queue", "Scout Users"].map((tab) => <button key={tab} onClick={() => setView(tab)} className={`rounded-full px-4 py-2 ${view === tab ? "bg-cyan-500 text-slate-950" : "text-cyan-200 hover:bg-white/10"}`}>{tab}</button>)}
          </div>
        </div>
      </section>

      {view === "Review Queue" ? (
        <>
          <section className="grid gap-4 md:grid-cols-5">
            <StatCard label="Review queue" value={totals.queue} hint="Submitted + HQ Review" />
            <StatCard label="Qualified" value={totals.qualified} hint="Ready for founder/demo" />
            <StatCard label="Won" value={totals.won} hint="Client won / commission active" />
            <StatCard label="Pipeline value" value={money(totals.pipeline)} hint="Monthly client value" />
            <StatCard label="Monthly commission" value={money(totals.commission)} hint="10% potential" />
          </section>

          <section className="grid gap-5 xl:grid-cols-[430px_1fr]">
            <div className="rounded-3xl border border-white/10 bg-slate-900/70 p-5">
              <div className="flex items-center justify-between gap-3">
                <div><div className="text-xs font-black uppercase tracking-[0.22em] text-cyan-300">HQ Review Queue</div><h2 className="mt-2 text-xl font-black">Scout submissions</h2></div>
                <button onClick={() => { const rows = loadScoutOpportunities(); setOpportunities(rows); setSelectedId(rows[0]?.id || ""); }} className="rounded-full border border-white/15 px-4 py-2 text-xs font-black text-cyan-200 hover:bg-white/10">Refresh</button>
              </div>
              <div className="mt-5 flex gap-2 overflow-x-auto pb-1 text-xs font-black">
                {["All", "Submitted", "HQ Review", "Qualified", "Meeting Scheduled", "Proposal Sent", "Client Won", "Commission Active", "Rejected"].map((x) => <button key={x} onClick={() => setFilter(x)} className={`shrink-0 rounded-full px-3 py-2 ${filter === x ? "bg-cyan-500 text-slate-950" : "bg-slate-950 text-slate-300"}`}>{x}</button>)}
              </div>
              <div className="mt-5 space-y-3">
                {visible.map((opp) => (
                  <button key={opp.id} onClick={() => setSelectedId(opp.id)} className={`w-full rounded-3xl border p-4 text-left transition ${selectedId === opp.id ? "border-cyan-300 bg-cyan-400/10" : "border-white/10 bg-slate-950/60 hover:border-cyan-300/40"}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div><div className="text-lg font-black text-white">{opp.name}</div><div className="mt-1 text-sm text-slate-400">{opp.scoutName} · {opp.layer}</div></div>
                      <span className={`rounded-full border px-3 py-1 text-xs font-black ${badgeClass(opp.stage)}`}>{opp.stage}</span>
                    </div>
                    <div className="mt-4 grid grid-cols-3 gap-2 text-sm">
                      <div className="rounded-2xl bg-white/5 p-3"><b>{money(opp.valueNumber)}</b><div className="text-xs text-slate-500">Value/mo</div></div>
                      <div className="rounded-2xl bg-white/5 p-3"><b>{money(opp.monthlyCommission)}</b><div className="text-xs text-slate-500">Scout/mo</div></div>
                      <div className="rounded-2xl bg-white/5 p-3"><b>{opp.score}/10</b><div className="text-xs text-slate-500">Score</div></div>
                    </div>
                    <div className="mt-3 text-xs font-bold text-slate-500">Updated {opp.updatedAt}</div>
                  </button>
                ))}
              </div>
            </div>

            {selected ? <div className="space-y-5">
              <section className="rounded-3xl border border-white/10 bg-slate-900/70 p-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div><div className="text-xs font-black uppercase tracking-[0.22em] text-cyan-300">Opportunity Detail</div><h2 className="mt-2 text-3xl font-black">{selected.name}</h2><p className="mt-1 text-sm text-slate-400">{selected.scoutName} · {selected.owner} · {selected.layer}</p></div>
                  <span className={`rounded-full border px-4 py-2 text-sm font-black ${badgeClass(selected.stage)}`}>{selected.stage}</span>
                </div>
                <div className="mt-6 grid gap-3 md:grid-cols-4">
                  <InfoBox label="Value / month" value={money(selected.valueNumber)} />
                  <InfoBox label="Scout commission" value={`${money(selected.monthlyCommission)} / month`} />
                  <InfoBox label="12 month potential" value={money(selected.monthlyCommission * 12)} />
                  <InfoBox label="Quality score" value={`${selected.score}/10`} />
                </div>
                <div className="mt-5 rounded-2xl border border-cyan-300/20 bg-cyan-400/10 p-4 text-sm text-cyan-100"><b>Next action:</b> {selected.nextAction}</div>
              </section>

              <section className="grid gap-5 lg:grid-cols-[1fr_360px]">
                <div className="rounded-3xl border border-white/10 bg-slate-900/70 p-6">
                  <h3 className="text-xl font-black">HQ decision controls</h3>
                  <div className="mt-4 grid gap-3 md:grid-cols-2">
                    <label className="text-sm font-bold text-slate-300">Status
                      <select value={selected.stage} onChange={(e) => updateOpportunity(selected.id, { stage: e.target.value })} className="mt-2 w-full rounded-2xl border border-white/10 bg-slate-950 px-4 py-3 text-white outline-none focus:border-cyan-300">
                        {statusFlow.map((x) => <option key={x}>{x}</option>)}
                      </select>
                    </label>
                    <label className="text-sm font-bold text-slate-300">Monthly value
                      <input type="number" value={selected.valueNumber} onChange={(e) => { const valueNumber = Number(e.target.value || 0); updateOpportunity(selected.id, { valueNumber, monthlyCommission: Math.round(valueNumber * ((selected.commissionPercent || 10) / 100)) }); }} className="mt-2 w-full rounded-2xl border border-white/10 bg-slate-950 px-4 py-3 text-white outline-none focus:border-cyan-300" />
                    </label>
                    <label className="text-sm font-bold text-slate-300">Commission %
                      <input type="number" value={selected.commissionPercent} onChange={(e) => { const commissionPercent = Number(e.target.value || 0); updateOpportunity(selected.id, { commissionPercent, monthlyCommission: Math.round(selected.valueNumber * (commissionPercent / 100)) }); }} className="mt-2 w-full rounded-2xl border border-white/10 bg-slate-950 px-4 py-3 text-white outline-none focus:border-cyan-300" />
                    </label>
                    <label className="text-sm font-bold text-slate-300">Next action
                      <input value={selected.nextAction} onChange={(e) => updateOpportunity(selected.id, { nextAction: e.target.value })} className="mt-2 w-full rounded-2xl border border-white/10 bg-slate-950 px-4 py-3 text-white outline-none focus:border-cyan-300" />
                    </label>
                  </div>
                  <div className="mt-5 flex flex-wrap gap-2">
                    <button onClick={() => updateOpportunity(selected.id, { stage: "Qualified", nextAction: "Prepare founder handoff or schedule meeting." })} className="rounded-full bg-emerald-500 px-4 py-2 text-sm font-black text-slate-950">Approve / Qualify</button>
                    <button onClick={() => updateOpportunity(selected.id, { stage: "HQ Review", nextAction: "Scout must add more information before approval." })} className="rounded-full bg-amber-400 px-4 py-2 text-sm font-black text-slate-950">Request Info</button>
                    <button onClick={() => updateOpportunity(selected.id, { stage: "Rejected", nextAction: "Opportunity rejected by HQ review." })} className="rounded-full bg-rose-500 px-4 py-2 text-sm font-black text-white">Reject</button>
                  </div>
                </div>

                <div className="rounded-3xl border border-white/10 bg-slate-900/70 p-6">
                  <h3 className="text-xl font-black">HQ notes</h3>
                  <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Write feedback for the scout..." className="mt-4 min-h-[120px] w-full rounded-2xl border border-white/10 bg-slate-950 px-4 py-3 text-sm text-white outline-none focus:border-cyan-300" />
                  <div className="mt-3 flex gap-2"><button onClick={() => addNote("MatrixOS HQ")} className="rounded-full bg-cyan-500 px-4 py-2 text-sm font-black text-slate-950">Add Note</button><button onClick={() => addNote("Request Info")} className="rounded-full border border-amber-300/30 px-4 py-2 text-sm font-black text-amber-200">Request Info</button></div>
                </div>
              </section>

              <section className="grid gap-5 lg:grid-cols-2">
                <div className="rounded-3xl border border-white/10 bg-slate-900/70 p-6"><h3 className="text-xl font-black">Scout discovery</h3><div className="mt-4 space-y-3"><InfoBox label="Challenge" value={selected.challenge} /><InfoBox label="Direct quote" value={selected.directQuote} /><InfoBox label="Observations" value={selected.observations} /></div></div>
                <div className="rounded-3xl border border-white/10 bg-slate-900/70 p-6"><h3 className="text-xl font-black">Business profile</h3><div className="mt-4 grid gap-3 sm:grid-cols-2"><InfoBox label="Industry" value={selected.industry} /><InfoBox label="Address" value={selected.address} /><InfoBox label="Role" value={selected.role} /><InfoBox label="Phone" value={selected.phone} /><InfoBox label="Email" value={selected.email} /><InfoBox label="Evidence" value={`${selected.evidence?.length || 0} type(s), ${selected.evidenceFiles?.length || 0} file(s)`} /></div></div>
              </section>

              <section className="rounded-3xl border border-white/10 bg-slate-900/70 p-6"><h3 className="text-xl font-black">Review timeline</h3><div className="mt-5 space-y-4 text-sm">{[...(selected.hqNotes || [])].reverse().map((n, i) => <div key={`${n.date}-${i}`} className="border-l-2 border-cyan-300 pl-4"><div className="text-xs font-black uppercase tracking-[0.16em] text-cyan-300">{n.by} · {n.date}</div><div className="mt-1 text-slate-200">{n.text}</div></div>)}{!(selected.hqNotes || []).length ? <div className="rounded-2xl border border-white/10 bg-slate-950/60 p-4 text-slate-400">No HQ notes yet.</div> : null}<div className="border-l-2 border-blue-400 pl-4"><div className="text-xs font-black uppercase tracking-[0.16em] text-blue-300">Submission received</div><div className="mt-1 text-slate-300">Scout submission is available for HQ review.</div></div></div></section>
            </div> : null}
          </section>
        </>
      ) : (
        <>
          <section className="grid gap-4 md:grid-cols-4">
            {[['Active scouts', scouts.length], ['Commission rule', '10%'], ['Commission window', '12 months'], ['Public route', '/scouts/login']].map(([label, value]) => <StatCard key={label} label={label} value={value} hint="Scout portal" />)}
          </section>
          <section className="rounded-3xl border border-white/10 bg-slate-900/70 p-6">
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3"><div><h2 className="text-xl font-black">Authenticated scout users</h2><p className="mt-1 text-sm text-slate-400">These credentials are used by the Scouts login page.</p></div><div className="flex gap-2"><button onClick={addScout} className="rounded-full border border-white/15 px-4 py-2 text-sm font-bold text-cyan-200 hover:bg-white/10">Add scout</button><button onClick={saveCredentials} className="rounded-full bg-cyan-500 px-4 py-2 text-sm font-black text-slate-950 hover:bg-cyan-300">Save credentials</button></div></div>
            <div className="overflow-x-auto"><table className="min-w-full text-left text-sm"><thead className="text-xs uppercase tracking-[0.18em] text-slate-400"><tr><th className="px-3 py-3">Name</th><th className="px-3 py-3">Username</th><th className="px-3 py-3">Password</th><th className="px-3 py-3">Level</th><th className="px-3 py-3">Region</th><th className="px-3 py-3">Status</th><th /></tr></thead><tbody>{scouts.map((row, index) => <tr key={row.id || index} className="border-t border-white/10">{[["name", "Name"], ["email", "Username"], ["password", "Password"], ["level", "Level"], ["region", "Region"], ["status", "Status"]].map(([field, placeholder]) => <td key={field} className="px-3 py-3"><input value={row[field] || ""} onChange={(e) => updateScout(index, field, e.target.value)} placeholder={placeholder} className="w-full min-w-[140px] rounded-xl border border-white/10 bg-slate-950 px-3 py-2 text-white outline-none focus:border-cyan-300" /></td>)}<td className="px-3 py-3"><button onClick={() => removeScout(index)} className="rounded-full border border-rose-300/20 px-3 py-2 text-xs font-bold text-rose-200 hover:bg-rose-400/10">Remove</button></td></tr>)}</tbody></table></div>
            {saved ? <div className="mt-4 rounded-2xl border border-emerald-300/20 bg-emerald-400/10 px-4 py-3 text-sm text-emerald-100">Saved. The scout can now sign in from /scouts/login.</div> : null}
          </section>
        </>
      )}
    </div>
  );
}
